import { gql } from "@apollo/client";
import { createRequestContext, getClient } from "lib/apolloClient";
import type {
  OIAFiltersInput,
  OIASortBy,
  OIATimePeriod,
  SingularProductCard
} from "@shared/graphql.types";
import { PRODUCT_CARD_PRICES_FRAGMENT } from "@shared/graphql.fragments";

type ReorderProductsPagination = {
  currentPage: number;
  perPage: number;
  total: number;
};

export type ReorderProductsQueryParams = {
  pagination: Omit<ReorderProductsPagination, "total">;
  sortOptions?: OIASortBy;
  filteringOptions?: OIAFiltersInput;
  period?: OIATimePeriod;
};

export type ReorderProductsArgs = {
  title: string;
  subtitle: string;
  products: SingularProductCard[];
  pagination: ReorderProductsPagination;
  sortOptions?: OIASortBy;
  filteringOptions?: OIAFiltersInput;
  period?: OIATimePeriod;
  showOrderItAgain: boolean;
};

const GET_ORDER_IT_AGAIN_PRODUCTS = gql`
  query getOrderItAgainProducts($orderItAgainInput: OrderItAgainInput) {
    getOrderItAgainProducts(orderItAgainInput: $orderItAgainInput) {
      title
      subtitle
      productCards {
        id
        type
        brandName
        title
        reviews {
          stars
          count
          bestRating
          worstRating
        }
        buttonText
        secondaryButton {
          text
          type
        }
        variants {
          sku
          href
          key
          slug
          availability {
            inStock
          }
          images {
            id
            description
            src
          }
          shade
          pricing {
            primary {
              formatted
              raw
              label
              currencySymbol
            }
            secondary {
              formatted
              raw
              label
              currencySymbol
            }
          }
          prices {
            ...ProductCardPricesFragment
          }
        }
      }
      filteringOptions {
        categories
      }
      period
      sortOptions
      pagination {
        currentPage
        perPage
        total
      }
      showOrderItAgain
    }
  }
  ${PRODUCT_CARD_PRICES_FRAGMENT}
`;

export const getOrderItAgainProducts = async ({
  pagination,
  sortOptions = "RECOMMENDED",
  filteringOptions = {
    categories: []
  },
  period = "ALL_TIME"
}: ReorderProductsQueryParams): Promise<ReorderProductsArgs> => {
  const result = await getClient().query<{
    getOrderItAgainProducts: {
      title: string;
      subtitle: string;
      productCards: SingularProductCard[];
      pagination: ReorderProductsArgs["pagination"];
      filteringOptions?: OIAFiltersInput;
      period?: OIATimePeriod;
      sortOptions?: OIASortBy;
      showOrderItAgain: boolean;
    };
  }>({
    query: GET_ORDER_IT_AGAIN_PRODUCTS,
    variables: {
      orderItAgainInput: {
        pagination,
        sortOptions,
        filteringOptions,
        period
      }
    },
    context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" }),
    fetchPolicy: "no-cache"
  });

  if (!result.data) throw new Error("No response received");

  const { getOrderItAgainProducts: oiaProducts } = result.data;

  return {
    title: oiaProducts.title,
    subtitle: oiaProducts.subtitle,
    products: oiaProducts.productCards,
    pagination: oiaProducts.pagination,
    filteringOptions: oiaProducts.filteringOptions,
    sortOptions: oiaProducts.sortOptions,
    period: oiaProducts.period,
    showOrderItAgain: oiaProducts.showOrderItAgain
  };
};
